import React, { useEffect, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { deleteNote, fetchUserNotes } from '../store/notesSlice';
import { Link } from 'react-router-dom';
import Sidebar from './Sidebar';
import { parseVerseId } from '../utils/verseUtils';
import OpenModalButton from "../components/OpenModalButton";
import LoginForm from "../components/LoginForm";
import SignupForm from "../components/SignupForm";
import { FiSearch } from 'react-icons/fi';

const NotesPage = () => {
  const dispatch = useDispatch();
  const user = useSelector((state) => state.session?.user);
  const { userNotes = [], loading, error } = useSelector((state) => state.notes || {});

  // 🔍 Search state
  const [searchQuery, setSearchQuery] = useState('');
  const [notePendingDelete, setNotePendingDelete] = useState(null);
  const [deletingId, setDeletingId] = useState(null);

  // Fetch user notes when the component loads
  useEffect(() => {
    if (user) {
      dispatch(fetchUserNotes());
    }
  }, [dispatch, user]);

  // 🔍 Filter notes based on search query
  const filteredNotes = userNotes.filter(note =>
    note.title.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.text.toLowerCase().includes(searchQuery.toLowerCase()) ||
    note.book.toLowerCase().includes(searchQuery.toLowerCase())
  );

  const handleDeleteNote = async () => {
    if (!notePendingDelete) return;

    try {
      setDeletingId(notePendingDelete.id);
      await dispatch(deleteNote(notePendingDelete.id)).unwrap();
      setNotePendingDelete(null);
    } catch (deleteError) {
      console.error('Failed to delete note:', deleteError);
    } finally {
      setDeletingId(null);
    }
  };

  if (!user) {
    return (
      <div className="container mx-auto p-6">
        <h1 className="text-3xl font-bold mb-6 text-center">My Notes</h1>
        <div className="bg-red-100 p-4 rounded-lg text-center">
          <p>
            Please{" "}
            <OpenModalButton
              buttonText={<span className="text-blue-700 hover:text-blue-500">log in</span>}
              modalComponent={<LoginForm />}
            />{" "}
            or{" "}
            <OpenModalButton
              buttonText={<span className="text-blue-700 hover:text-blue-500">sign up</span>}
              modalComponent={<SignupForm />}
            />{" "}
            to view your notes.
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="relative flex min-h-screen bg-gray-50">
      {/* Sidebar */}
      <Sidebar />

      {/* Main Content */}
      <div className="flex-1 p-6 lg:p-8 ml-[80px]">
        <h1 className="text-4xl font-bold text-gray-900 text-center mb-8">My Notes</h1>

        {/* 🔍 Search Bar */}
        <div className="relative max-w-lg mx-auto mb-6">
          <FiSearch className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-500" />
          <input
            type="text"
            placeholder="Search notes..."
            value={searchQuery}
            onChange={(e) => setSearchQuery(e.target.value)}
            className="pl-10 pr-4 py-2 w-full border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500 focus:outline-none"
          />
        </div>

        {/* Loading & Error Handling */}
        {loading && <p className="text-center text-blue-500 animate-pulse">Loading notes...</p>}
        {error && <p className="text-center text-red-500">{error}</p>}

        {/* Empty State */}
        {!loading && !error && userNotes.length === 0 && (
          <div className="flex flex-col items-center justify-center py-12">
            <img src="/images/empty-notes.svg" alt="No Notes" className="w-48 h-48 mb-4" />
            <p className="text-gray-600 text-lg">You have no notes yet. Start adding notes!</p>
          </div>
        )}

        {/* No Results Found Message for Search */}
        {!loading && !error && userNotes.length > 0 && filteredNotes.length === 0 && (
          <p className="text-center text-gray-500">No matching notes found.</p>
        )}

        {/* Notes Grid */}
        {filteredNotes.length > 0 && (
          <div className="grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 gap-6">
            {filteredNotes.map((note) => {
              const { chapter, verse } = parseVerseId(note.verse_id);

              return (
                <div
                  key={note.id}
                  className="relative bg-white p-5 rounded-lg shadow-md hover:shadow-xl transition-transform transform hover:-translate-y-1 border border-gray-200"
                >
                  <button
                    type="button"
                    onClick={() => setNotePendingDelete(note)}
                    className="absolute right-4 top-4 rounded-md border border-red-200 px-3 py-1 text-sm text-red-700 hover:bg-red-50"
                  >
                    Delete
                  </button>

                  <Link
                    to={`/bible/${note.version}/${note.book}/${chapter}`}
                    className="group block"
                  >
                    <h3 className="pr-20 text-xl font-semibold text-gray-800 group-hover:text-blue-600">
                      {note.title || 'Untitled Note'}
                    </h3>
                    <p className="mt-2 text-gray-700 line-clamp-2">{note.text}</p>

                    <div className="mt-4 text-gray-500 text-sm flex justify-between">
                      <span>{note.book} {chapter}:{verse}</span>
                      <span>{new Date(note.created_at).toLocaleDateString()}</span>
                    </div>
                  </Link>
                </div>
              );
            })}
          </div>
        )}
      </div>

      {notePendingDelete && (
        <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/50 p-4">
          <div className="w-full max-w-sm rounded-lg bg-white p-6 shadow-lg">
            <p className="text-lg font-semibold text-gray-900">Delete this memo?</p>
            <p className="mt-2 text-sm text-gray-600">
              This will permanently remove
              {notePendingDelete.title ? ` "${notePendingDelete.title}"` : ' this note'}.
            </p>
            <div className="mt-6 flex justify-end gap-2">
              <button
                type="button"
                onClick={() => setNotePendingDelete(null)}
                className="rounded-md bg-gray-200 px-4 py-2 text-sm text-gray-800 hover:bg-gray-300"
              >
                Cancel
              </button>
              <button
                type="button"
                onClick={handleDeleteNote}
                disabled={deletingId === notePendingDelete.id}
                className="rounded-md bg-red-600 px-4 py-2 text-sm text-white hover:bg-red-700 disabled:cursor-not-allowed disabled:opacity-60"
              >
                {deletingId === notePendingDelete.id ? 'Deleting...' : 'Delete'}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default NotesPage;
